import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Activity, Menu, X, LogOut, Crown } from "lucide-react";

const links = [
  { to: "/signals", label: "Signals", id: "nav-signals-link" },
  { to: "/performance", label: "Performance", id: "nav-performance-link" },
  { to: "/pricing", label: "Pricing", id: "nav-pricing-link" },
];

export const Navbar = () => {
  const { user, openAuth, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  const nav = useNavigate();

  const isActive = (to) => loc.pathname === to;

  return (
    <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-[#080B11]/85 border-b border-slate-800/60">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 h-16 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo-link" className="flex items-center gap-2 group">
          <span className="relative flex h-3 w-3">
            <span className="qs-pulse absolute inline-flex h-3 w-3 rounded-full bg-emerald-500" />
            <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-400" />
          </span>
          <span className="font-display font-extrabold text-lg tracking-tight">
            Quantum<span className="text-gradient">Signal</span><span className="text-slate-500"> AI</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={l.id}
              className={`text-sm font-medium transition-colors hover:text-emerald-400 ${
                isActive(l.to) ? "text-emerald-400" : "text-slate-300"
              }`}
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <div className="flex items-center gap-2 text-xs font-mono-ticker text-slate-400 mr-1">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
            90.0% WIN RATE
          </div>
          {user ? (
            <>
              {user.subscription_active && (
                <span className="flex items-center gap-1 text-xs font-semibold text-amber-400">
                  <Crown size={14} /> {user.plan || "Pro"}
                </span>
              )}
              <Button
                data-testid="nav-dashboard-button"
                onClick={() => nav("/signals")}
                className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-full h-9 px-5"
              >
                Dashboard
              </Button>
              <Button
                data-testid="nav-logout-button"
                variant="ghost"
                onClick={logout}
                className="text-slate-400 hover:text-white h-9 w-9 p-0"
              >
                <LogOut size={18} />
              </Button>
            </>
          ) : (
            <>
              <Button
                data-testid="nav-login-button"
                variant="ghost"
                onClick={() => openAuth("login")}
                className="text-slate-200 hover:text-white hover:bg-slate-800 rounded-full h-9 px-5"
              >
                Login
              </Button>
              <Button
                data-testid="nav-signup-button"
                onClick={() => openAuth("register")}
                className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-full h-9 px-5 shadow-lg shadow-emerald-500/20"
              >
                Get Signals
              </Button>
            </>
          )}
        </div>

        <button
          data-testid="nav-mobile-toggle"
          className="md:hidden text-slate-200"
          onClick={() => setOpen((o) => !o)}
        >
          {open ? <X /> : <Menu />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-slate-800/60 bg-[#080B11] px-6 py-4 space-y-4">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              onClick={() => setOpen(false)}
              className="block text-slate-200 font-medium"
            >
              {l.label}
            </Link>
          ))}
          {user ? (
            <Button onClick={() => { setOpen(false); logout(); }} className="w-full bg-slate-800">Logout</Button>
          ) : (
            <div className="flex gap-3">
              <Button onClick={() => { setOpen(false); openAuth("login"); }} variant="ghost" className="flex-1 bg-slate-800">Login</Button>
              <Button onClick={() => { setOpen(false); openAuth("register"); }} className="flex-1 bg-emerald-500">Sign Up</Button>
            </div>
          )}
        </div>
      )}
    </header>
  );
};
